import React, { useEffect, useRef } from "react";
import { Sheet } from "@mui/joy";
import * as _ from "lodash";
import { useRecoilState, useRecoilValue } from "recoil";
import { layoutAtom } from "recoil/layoutAtom";
import { usePrevious } from "@uidotdev/usehooks";
import Activity from "./Activity";
import ThoughtRecord from "./thought-record/ThoughtRecord";
import Payment from "./payment/Payment";
import Chat from "./coaching/Chat";
import MyThoughtMap from "./my-thoughtmap/MyThoughtMap";
import MeditationRecord from "./meditation-record/MeditationRecord";
import MyValueCompass from "./my-value-compass/MyValueCompass";
import { axiosInstance } from "api";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { taskListAtom } from "recoil/taskListAtom";
import useIsInappWebview from "hooks/useIsInappWebview";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import ThoughtRecordMobileListPage from "./thought-record/ThoughtRecordMobileListPage";
import ThoughtRecordMobileDetailPage from "./thought-record/ThoughtRecordMobileDetailPage";
import MeditationRecordMobileDetailPage from "./meditation-record/MeditationRecordMobileDetailPage";
import MeditationRecordMobileListPage from "./meditation-record/MeditationRecordMobileListPage";
import MeditationRecordMobileStatisticsPage from "./meditation-record/MeditationRecordMobileStatisticsPage";
import MyPage from "../../pages/mypage/MyPage";
import AccountPage from "../../pages/mypage/AccountPage";
import PaymentHistoryPage from "../../pages/mypage/PaymentHistoryPage";
import NotificationSettingsPage from "../../pages/mypage/NotificationSettingsPage";
import NotificationSettingsUpdatePage from "../../pages/mypage/NotificationSettingsUpdatePage";
import SupportFaqListPage from "../../pages/mypage/SupportFaqListPage";
import SupportFaqDetailPage from "../../pages/mypage/SupportFaqDetailPage";
import SecuritySettingsTask from "../../pages/security/SecuritySettingsTask";
import PinSetupTask from "../../pages/security/PinSetupTask";
import AppLockUnlockScreen from "../../pages/security/AppLockUnlockScreen";
import MyProgressV2 from "./my-progress/MyProgressV2";
import Retrospect from "./Retrospect";
import PlanUpgradeScreen from "./payment/PlanUpgradeScreen";

export interface VisibleIndexType {
  min?: number;
  max?: number;
}

export default function Task({ taskKey }: { taskKey: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutState, setLayoutState] = useRecoilState(layoutAtom);
  const previous_layoutState = usePrevious(layoutState);

  const patientState = useRecoilValue(patientAtom);

  const handleResize = () => {
    const containerWidth = containerRef.current?.offsetWidth || 0;

    if (containerWidth > 0) {
      if (
        (layoutState.isTaskContainerSmall && containerWidth >= 800) ||
        (!layoutState.isTaskContainerSmall && containerWidth < 800)
      ) {
        setLayoutState((state) => ({
          ...state,
          isTaskContainerSmall: containerWidth < 800,
        }));
      }
    }
  };

  useEffect(() => {
    if (layoutState && previous_layoutState) {
      const layoutState_copy = _.cloneDeep(layoutState);
      const previous_layoutState_copy = _.cloneDeep(previous_layoutState);
      delete layoutState_copy.isTaskContainerSmall;
      delete previous_layoutState_copy.isTaskContainerSmall;
      if (!_.isEqual(layoutState_copy, previous_layoutState_copy)) {
        handleResize();
      }
    } else if (!previous_layoutState) {
      handleResize();
    }
  }, [layoutState]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const user = useRecoilValue(userAtom);
  const taskListState = useRecoilValue(taskListAtom);
  const isInappWebview = useIsInappWebview();

  const { isSubscribing } = useSubscriptionStatus();

  const isPatient = !!user?.accessToken && user?.role === "patient";
  const showPaymentModule = !user?.accessToken
    ? !isInappWebview
    : isPatient && !isSubscribing;

  return (
    <Sheet
      className={"w-full h-full grow flex flex-col "}
      sx={{ backgroundColor: "background.level1" }}
      ref={containerRef}
    >
      {((!!axiosInstance.defaults.headers["X-Auth-Token"] &&
        !!user?.accessToken &&
        !!patientState?.patientId) ||
        !isInappWebview) &&
      taskListState &&
      taskListState.length > 0 &&
      taskKey === "payment" ? (
        <Payment />
      ) : taskKey === "chat" ? (
        <Chat
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "thoughtmap" ? (
        <MyThoughtMap showPaymentModule={showPaymentModule} />
      ) : taskKey === "valueCompass" ? (
        <MyValueCompass showPaymentModule={showPaymentModule} />
      ) : taskKey === "progress" ? (
        <MyProgressV2 />
      ) : taskKey === "mypage" ? (
        <MyPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "account" ? (
        <AccountPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "payment-history" ? (
        <PaymentHistoryPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "notification-settings" ? (
        <NotificationSettingsPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "notification-settings-update" ? (
        <NotificationSettingsUpdatePage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "security-settings" ? (
        <SecuritySettingsTask
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "pin-setup" ? (
        <PinSetupTask
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "unlock" ? (
        <AppLockUnlockScreen />
      ) : taskKey === "support" ? (
        <SupportFaqListPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "support-faq" ? (
        <SupportFaqDetailPage
          key={`${user?.userId || "noUserId"}_${patientState?.patientId || "noPatientId"}`}
        />
      ) : taskKey === "thought-records" ? (
        <ThoughtRecordMobileListPage />
      ) : taskKey === "thought-record-detail" ? (
        <ThoughtRecordMobileDetailPage />
      ) : taskKey === "meditation-records" ? (
        <MeditationRecordMobileListPage />
      ) : taskKey === "meditation-record-detail" ? (
        <MeditationRecordMobileDetailPage />
      ) : taskKey === "meditation-record-statistics" ? (
        <MeditationRecordMobileStatisticsPage />
      ) : taskKey.includes("0-3-W") ? (
        <Retrospect taskKey={taskKey} />
      ) : taskKey.includes("2-2-T") ? (
        <ThoughtRecord taskKey={taskKey} />
      ) : taskKey.includes("5-7-T") ? (
        <MeditationRecord taskKey={taskKey} />
      ) : taskKey === "plan-upgrade" ? (
        <PlanUpgradeScreen />
      ) : (
        <Activity taskKey={taskKey} />
      )}
    </Sheet>
  );
}
